import t from 'react-translate';
import { useContext, useState } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { omit } from 'underscore';

import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import RolePlayContextSetting from './role-play-context-setting';
import CharacterSetting from './ai-character-setting';
import LearnerObjectiveSetting from './ai-learner-objective-setting';
import { AIConfigStep } from '..';
import EditAiInstructionContext from './context';

type SetUpInstructionProps = {
  setStep?: Function;
  isUpdate?: boolean;
  onSubmit?: Function;
};

const styles = css`
  .btn-container {
    gap: ${halfSpacing}px;
  }
`;

enum SubmitType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
}

const SetUpInstruction = (props: SetUpInstructionProps) => {
  const {
    setStep,
    isUpdate = false,
    onSubmit,
  } = props || {};
  const { getValues, formState } = useFormContext();
  const { forwardOnModalCloseRef, setShowEditAiInstruction } = useContext(EditAiInstructionContext);

  const [submitting, setSubmitting] = useState(false);
  const { isValid, isDirty } = formState;
  const isSubmitDisabled = !isValid || !isDirty;

  const getPayload = (formData, submitType) => {
    const cleanedFormData = omit(formData,
      [
        'requiredAiLearnerGoal',
        'aiCharacterObjections',
        'aiLearnerGoals',
      ]);

    switch (submitType) {
      case SubmitType.CREATE:
      {
        const aiCharacterObjections = [
          ...formData.aiCharacterObjections
            .map((item) => (item?.text))
            .filter((text) => text && text.trim() !== ''),
        ];

        const aiLearnerGoals = [
          formData.requiredAiLearnerGoal.text,
          ...formData.aiLearnerGoals
            .map((item) => (item?.text))
            .filter((text) => text && text.trim() !== ''),
        ];
        return {
          ...cleanedFormData,
          aiCharacterObjections,
          aiLearnerGoals,
        };
      }
      case SubmitType.UPDATE:
        return {
          ...cleanedFormData,
        };
      default:
        return {
          ...cleanedFormData,
        };
    }
  };

  const handleCreateRolePlay = () => {
    const payload = getPayload(getValues(), SubmitType.CREATE);
    setSubmitting(true);
    onSubmit({
      aiRolePlay: {
        ...payload,
      },
    }, false, ComponentType.AI_ROLE_PLAY).then(() => {
      setSubmitting(false);
    });
  };

  const handleUpdateRolePlay = () => {
    const payload = getPayload(getValues(), SubmitType.UPDATE);
    setSubmitting(true);
    onSubmit({
      ...payload,
    }).then(() => {
      setSubmitting(false);
      setShowEditAiInstruction(false);
    });
  };

  const getSubmitSection = () => {
    if (isUpdate) {
      return (
        <div className='d-flex pt-8 pb-6 justify-content-center btn-container'>
          <Button
            variant='secondary'
            type='button'
            onClick={() => {
              if (forwardOnModalCloseRef.current) {
                forwardOnModalCloseRef.current();
              }
            }}
          >
            {t.FORM.CANCEL()}
          </Button>
          <Button
            variant='primary'
            type='button'
            disabled={isSubmitDisabled || submitting}
            onClick={handleUpdateRolePlay}
          >
            {t.FORM.UPDATE()}
          </Button>
        </div>
      );
    }

    return (
      <div className='d-flex pt-8 pb-6 justify-content-center btn-container'>
        <Button
          variant='secondary'
          type='button'
          onClick={() => setStep && setStep(AIConfigStep.SET_UP_BASICS)}
        >
          {t.FORM.BACK()}
        </Button>
        <Button
          variant='primary'
          type='button'
          disabled={isSubmitDisabled || submitting}
          onClick={handleCreateRolePlay}
        >
          {t.FORM.CREATE()}
        </Button>
      </div>
    );
  };

  return (
    <div css={styles}>
      <RolePlayContextSetting />
      <CharacterSetting />
      <LearnerObjectiveSetting />
      {getSubmitSection()}
    </div>
  );
};

export default SetUpInstruction;
